import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useTranslation } from "react-i18next";
import { Checkbox, Input, Radio } from "antd";
import {
  OPEN_TEXT_LENGTH,
  QUESTION_TYPE,
  RATING_SCALE,
  RATING_SCALE_META,
  SURVEY_FLOW_MODE
} from "./constants";
import SurveyProgressIndicator from "./SurveyProgressIndicator";
import LocationAnswer from "./LocationAnswer";
import PhoneAnswer from "./PhoneAnswer";
import VideoAnswer from "./VideoAnswer";
import {
  AUTO_ADVANCE_MS,
  OPEN_TEXT_MAX_LENGTH,
  SHORT_TEXT_MAX_LENGTH,
  findFirstInvalidQuestion,
  getTotalSteps,
  isAnswerValid,
  isContactStepIndex,
  isQuestionAnswered,
  isSteppedFlow,
  supportsAutoAdvance
} from "./surveyWidgetFlow";
import styles from "./SurveyWidgetPanel.module.scss";

const { TextArea } = Input;

const POWERED_BY_LOGO = {
  light: "/assets/pivony_powered_by_logo_light.png",
  dark: "/assets/pivony_powered_by_logo_dark.png"
};

const DEFAULT_PRIMARY = "#6366f1";

function hexToRgb(hex) {
  const raw = String(hex || "").replace("#", "");
  if (raw.length !== 6) return null;
  const n = parseInt(raw, 16);
  if (Number.isNaN(n)) return null;
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function softTint(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return "#eef2ff";
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.12)`;
}

const SurveyWidgetPanel = ({
  title = "",
  description = "",
  questions = [],
  widgetHeaderTitle = "",
  logoUrl = "",
  showBranding = true,
  requestContactDetails = false,
  contactDetailsRequired = false,
  primaryColor = DEFAULT_PRIMARY,
  flowMode = SURVEY_FLOW_MODE.STEPPED,
  thankYouMessage = "",
  interactive = false,
  answers = {},
  onAnswerChange,
  contact = {},
  onContactFieldChange,
  contactErrors = {},
  onContactEmailBlur,
  submitting = false,
  submitted = false,
  submitError = "",
  onSubmit,
  embed = false,
  captchaSlot = null,
  // Submit stays disabled while something on the last step is missing (the consent box).
  submitDisabled = false,
  gateSlot = null,
  consentSlot = null,
  // What a video question needs to upload on the public page (see VideoAnswer); absent in the builder.
  videoUpload = null,
  // "Last day: …" under the introduction when the survey shows its deadline (public page only).
  deadlineNote = "",
  // The public page's check of a one-response-per-email address: async (email) => true when it
  // already answered, false when not, null when it could not tell. Absent in the builder.
  checkEmailTaken = null,
  compact = false,
  previewMode = false,
  maxPreviewQuestions
}) => {
  const { t, i18n } = useTranslation();
  const panelRef = useRef(null);
  const advanceTimer = useRef(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [stepAnimKey, setStepAnimKey] = useState(0);
  const [shakeTarget, setShakeTarget] = useState(null);
  const [bounceQuestionId, setBounceQuestionId] = useState(null);
  // Questions whose answer was checked (Next, Submit or leaving the phone field): their errors show.
  const [checkedIds, setCheckedIds] = useState({});
  const markChecked = useCallback(
    questionId =>
      setCheckedIds(prev =>
        prev[questionId] ? prev : { ...prev, [questionId]: true }
      ),
    []
  );

  const accent = (primaryColor || DEFAULT_PRIMARY).trim() || DEFAULT_PRIMARY;
  const headerTitle = (widgetHeaderTitle || "").trim();
  const brandLogo = (logoUrl || "").trim();
  const surveyTitle = (title || "").trim();
  const headerText = brandLogo ? surveyTitle : headerTitle;
  const showHeaderBar = !!(brandLogo || headerText);
  const stepped = isSteppedFlow(flowMode);

  const panelStyle = useMemo(
    () => ({
      "--survey-primary": accent,
      "--survey-primary-soft": softTint(accent),
      "--survey-primary-text": "#4338ca"
    }),
    [accent]
  );

  // The builder's stepped preview pages through every question with Back / Next (no answer
  // checks); only the one-page preview is cut to maxPreviewQuestions.
  const browsable = stepped && previewMode && !interactive;
  const visibleQuestions =
    typeof maxPreviewQuestions === "number" && !browsable
      ? questions.slice(0, maxPreviewQuestions)
      : questions;
  const hiddenCount =
    typeof maxPreviewQuestions === "number" && !browsable
      ? Math.max(0, questions.length - maxPreviewQuestions)
      : 0;
  const questionCount = visibleQuestions.length;
  const hasContactStep = requestContactDetails;
  const totalSteps = getTotalSteps(questionCount, hasContactStep);
  const previewStep = stepped ? 0 : null;
  const navigable = stepped && (interactive || browsable);
  // A preview step past the end (questions deleted in the builder) shows the last one.
  const stepIndex = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const activeStep = navigable ? stepIndex : previewStep;
  const onContactStep =
    stepped &&
    activeStep !== null &&
    isContactStepIndex(activeStep, questionCount);
  const currentQuestion =
    stepped && activeStep !== null && !onContactStep
      ? visibleQuestions[activeStep]
      : null;

  const progressCurrent =
    stepped && activeStep !== null ? Math.min(activeStep + 1, totalSteps) : 0;

  useEffect(
    () => () => {
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
    },
    []
  );

  useEffect(() => {
    if (shakeTarget) {
      const timer = setTimeout(() => setShakeTarget(null), 500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [shakeTarget]);

  const scrollToPanelTop = useCallback(() => {
    if (panelRef.current && panelRef.current.scrollIntoView) {
      panelRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, []);

  const goToStep = useCallback(nextStep => {
    setCurrentStep(nextStep);
    setStepAnimKey(k => k + 1);
  }, []);

  const goNext = useCallback(() => {
    if (!navigable) return;
    if (stepIndex < totalSteps - 1) goToStep(stepIndex + 1);
  }, [navigable, stepIndex, totalSteps, goToStep]);

  const goBack = useCallback(() => {
    if (!navigable || stepIndex <= 0) return;
    goToStep(stepIndex - 1);
  }, [navigable, stepIndex, goToStep]);

  const validateCurrentStep = useCallback(() => {
    if (!stepped) return true;
    if (onContactStep) {
      if (!contactDetailsRequired) return true;
      const missing =
        !contact.firstName?.trim() ||
        !contact.lastName?.trim() ||
        !contact.email?.trim() ||
        contactErrors.email;
      if (missing) {
        setShakeTarget("contact");
        scrollToPanelTop();
        return false;
      }
      return true;
    }
    if (
      currentQuestion &&
      ((currentQuestion.required &&
        !isQuestionAnswered(currentQuestion, answers)) ||
        !isAnswerValid(currentQuestion, answers))
    ) {
      markChecked(currentQuestion.id);
      setShakeTarget(currentQuestion.id);
      scrollToPanelTop();
      return false;
    }
    return true;
  }, [
    markChecked,
    stepped,
    onContactStep,
    contactDetailsRequired,
    contact,
    contactErrors.email,
    currentQuestion,
    answers,
    scrollToPanelTop
  ]);

  const scheduleAutoAdvance = useCallback(
    question => {
      if (!stepped || !interactive || !supportsAutoAdvance(question?.type))
        return;
      if (advanceTimer.current) clearTimeout(advanceTimer.current);
      advanceTimer.current = setTimeout(() => {
        if (currentStep < totalSteps - 1) goToStep(currentStep + 1);
      }, AUTO_ADVANCE_MS);
    },
    [stepped, interactive, currentStep, totalSteps, goToStep]
  );

  const setAnswer = useCallback(
    (questionId, value, question) => {
      if (!interactive || !onAnswerChange) return;
      onAnswerChange(questionId, value);
      if (question?.type === QUESTION_TYPE.RATING) {
        setBounceQuestionId(questionId);
        setTimeout(() => setBounceQuestionId(null), 200);
      }
      scheduleAutoAdvance(question);
    },
    [interactive, onAnswerChange, scheduleAutoAdvance]
  );

  // Addresses the API said already answered, by question id (so editing the field clears the note).
  const [takenEmails, setTakenEmails] = useState({});
  const [checkingEmail, setCheckingEmail] = useState(false);
  const emailIsTaken = question =>
    !!takenEmails[question.id] &&
    takenEmails[question.id] === String(answers[question.id] || "").trim().toLowerCase();

  const checkEmailAnswer = async question => {
    const email = String(answers[question.id] || "").trim();
    if (!checkEmailTaken || !email || !isAnswerValid(question, answers)) return false;
    setCheckingEmail(true);
    let taken = null;
    try {
      taken = await checkEmailTaken(email);
    } catch (_) {
      taken = null;
    } finally {
      setCheckingEmail(false);
    }
    setTakenEmails(prev => ({ ...prev, [question.id]: taken ? email.toLowerCase() : "" }));
    return taken === true;
  };

  const handleNext = async () => {
    if (interactive && !validateCurrentStep()) return;
    if (interactive && currentQuestion?.type === QUESTION_TYPE.EMAIL && (await checkEmailAnswer(currentQuestion))) {
      setShakeTarget(currentQuestion.id);
      return;
    }
    goNext();
  };

  const handlePrimarySubmit = () => {
    if (stepped && !validateCurrentStep()) return;
    if (!stepped) {
      const invalid = findFirstInvalidQuestion(visibleQuestions, answers);
      if (invalid) {
        markChecked(invalid.id);
        setShakeTarget(invalid.id);
        scrollToPanelTop();
        return;
      }
    }
    if (onSubmit) onSubmit();
  };

  const handleKeyDown = useCallback(
    event => {
      if (!interactive || submitted) return;
      if (event.key === "Enter" && !event.shiftKey) {
        const tag = (event.target?.tagName || "").toLowerCase();
        if (tag === "textarea" || tag === "select") return;
        // Enter picks an item in the country and city lists; it must not move on or submit.
        if (event.target?.closest?.(".ant-select")) return;
        event.preventDefault();
        const onManualStep =
          stepped &&
          currentQuestion &&
          !supportsAutoAdvance(currentQuestion.type) &&
          currentStep < totalSteps - 1;
        const onLast = !stepped || currentStep >= totalSteps - 1;
        if (onManualStep) handleNext();
        else if (onLast) handlePrimarySubmit();
        return;
      }
      if (
        !stepped ||
        !currentQuestion ||
        currentQuestion.type !== QUESTION_TYPE.RATING
      ) {
        return;
      }
      const keyNum = Number(event.key);
      if (!Number.isInteger(keyNum)) return;
      const scale =
        currentQuestion.ratingScale ||
        currentQuestion.scale ||
        RATING_SCALE.ONE_TO_FIVE;
      const meta =
        RATING_SCALE_META[scale] || RATING_SCALE_META[RATING_SCALE.ONE_TO_FIVE];
      if (keyNum < meta.min || keyNum > meta.max) return;
      event.preventDefault();
      setAnswer(currentQuestion.id, keyNum, currentQuestion);
    },
    [
      interactive,
      submitted,
      stepped,
      currentStep,
      totalSteps,
      currentQuestion,
      setAnswer,
      handleNext,
      handlePrimarySubmit
    ]
  );

  useEffect(() => {
    const node = panelRef.current;
    if (!node || !interactive) return undefined;
    node.addEventListener("keydown", handleKeyDown);
    return () => node.removeEventListener("keydown", handleKeyDown);
  }, [interactive, handleKeyDown]);

  const renderRating = question => {
    const scale =
      question.ratingScale || question.scale || RATING_SCALE.ONE_TO_FIVE;
    const meta =
      RATING_SCALE_META[scale] || RATING_SCALE_META[RATING_SCALE.ONE_TO_FIVE];
    const values = [];
    for (let i = meta.min; i <= meta.max; i += 1) values.push(i);
    const pillClass = meta.max > 5 ? styles.ratingPillSmall : styles.ratingPill;
    return (
      <div className={styles.ratingRow} role="group" aria-label={question.text}>
        {values.map(value => {
          const active = answers[question.id] === value;
          return (
            <button
              key={value}
              type="button"
              disabled={!interactive}
              aria-label={`${value}`}
              aria-pressed={active}
              className={[
                pillClass,
                active ? styles.ratingPillActive : "",
                bounceQuestionId === question.id && active
                  ? styles.ratingPillBounce
                  : ""
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => setAnswer(question.id, value, question)}
            >
              {value}
            </button>
          );
        })}
      </div>
    );
  };

  const renderQuestion = (question, index, options = {}) => {
    const { showIndex = true } = options;
    const isShaking = shakeTarget === question.id;
    return (
      <div
        key={question.id || index}
        className={[
          styles.questionBlock,
          isShaking ? styles.questionBlockError : ""
        ]
          .filter(Boolean)
          .join(" ")}
        data-question-id={question.id}
      >
        <p className={styles.questionText} id={`q-label-${question.id}`}>
          {showIndex ? `${index + 1}. ` : ""}
          {question.text}
          {question.required ? (
            <span className={styles.required} aria-hidden="true">
              *
            </span>
          ) : null}
          {question.required ? (
            <span className={styles.srOnly}>
              {t("surveyManagement.required")}
            </span>
          ) : null}
        </p>
        {question.type === QUESTION_TYPE.RATING ? renderRating(question) : null}
        {question.type === QUESTION_TYPE.SINGLE_CHOICE ? (
          interactive ? (
            <Radio.Group
              className={styles.choiceGroup}
              value={answers[question.id]}
              onChange={e => setAnswer(question.id, e.target.value, question)}
              aria-labelledby={`q-label-${question.id}`}
            >
              {(question.options || []).map(opt => (
                <Radio key={opt} value={opt}>
                  {opt}
                </Radio>
              ))}
            </Radio.Group>
          ) : (
            <div className={styles.previewChoiceList}>
              {(question.options || ["Option 1", "Option 2"]).map(
                (opt, optIdx) => (
                  <label
                    key={`${question.id}-${optIdx}`}
                    className={styles.previewChoice}
                  >
                    <span className={styles.previewRadio} />
                    <span>{opt}</span>
                  </label>
                )
              )}
            </div>
          )
        ) : null}
        {question.type === QUESTION_TYPE.MULTIPLE_CHOICE ? (
          interactive ? (
            <Checkbox.Group
              className={styles.choiceGroup}
              options={question.options || []}
              value={answers[question.id] || []}
              onChange={vals => setAnswer(question.id, vals, question)}
              aria-labelledby={`q-label-${question.id}`}
            />
          ) : (
            <div className={styles.previewChoiceList}>
              {(question.options || ["Option 1", "Option 2"]).map(
                (opt, optIdx) => (
                  <label
                    key={`${question.id}-${optIdx}`}
                    className={styles.previewChoice}
                  >
                    <span className={styles.previewCheckbox} />
                    <span>{opt}</span>
                  </label>
                )
              )}
            </div>
          )
        ) : null}
        {question.type === QUESTION_TYPE.PHONE ? (
          <>
            <PhoneAnswer
              value={answers[question.id]}
              onChange={value => setAnswer(question.id, value, question)}
              onBlur={() => answers[question.id] && markChecked(question.id)}
              language={i18n.language}
              placeholder={t("surveyManagement.widget.phone_placeholder")}
              labelledBy={`q-label-${question.id}`}
              required={question.required}
              invalid={
                checkedIds[question.id] && !isAnswerValid(question, answers)
              }
              disabled={!interactive}
            />
            {checkedIds[question.id] && !isAnswerValid(question, answers) ? (
              <p className={styles.fieldError} role="alert">
                {t("surveyManagement.widget.phone_invalid")}
              </p>
            ) : null}
          </>
        ) : null}
        {question.type === QUESTION_TYPE.EMAIL ? (
          <>
            <Input
              type="email"
              inputMode="email"
              autoComplete="email"
              value={interactive ? answers[question.id] || "" : undefined}
              readOnly={!interactive}
              maxLength={254}
              aria-required={question.required || undefined}
              aria-labelledby={`q-label-${question.id}`}
              aria-invalid={
                checkedIds[question.id] && !isAnswerValid(question, answers)
                  ? true
                  : undefined
              }
              onChange={e => setAnswer(question.id, e.target.value, question)}
              onBlur={() => {
                if (!answers[question.id]) return;
                markChecked(question.id);
                // On a one-page survey the check runs when the field is left.
                if (!stepped) checkEmailAnswer(question);
              }}
              placeholder={t("surveyManagement.widget.email_placeholder")}
            />
            {checkedIds[question.id] && !isAnswerValid(question, answers) ? (
              <p className={styles.fieldError} role="alert">
                {t("surveyManagement.widget.email_invalid")}
              </p>
            ) : emailIsTaken(question) ? (
              <p className={styles.fieldError} role="alert">
                {t("surveyManagement.public.already_responded")}
              </p>
            ) : null}
          </>
        ) : null}
        {question.type === QUESTION_TYPE.URL ? (
          <>
            <Input
              type="url"
              inputMode="url"
              autoComplete="url"
              value={interactive ? answers[question.id] || "" : undefined}
              readOnly={!interactive}
              maxLength={2000}
              aria-required={question.required || undefined}
              aria-labelledby={`q-label-${question.id}`}
              aria-invalid={
                checkedIds[question.id] && !isAnswerValid(question, answers)
                  ? true
                  : undefined
              }
              onChange={e => setAnswer(question.id, e.target.value, question)}
              onBlur={() => answers[question.id] && markChecked(question.id)}
              placeholder={
                question.urlDomain
                  ? `https://${question.urlDomain}/…`
                  : t("surveyManagement.widget.url_placeholder")
              }
            />
            {checkedIds[question.id] && !isAnswerValid(question, answers) ? (
              <p className={styles.fieldError} role="alert">
                {question.urlDomain
                  ? t("surveyManagement.widget.url_invalid_domain", { domain: question.urlDomain })
                  : t("surveyManagement.widget.url_invalid")}
              </p>
            ) : null}
          </>
        ) : null}
        {question.type === QUESTION_TYPE.VIDEO ? (
          <VideoAnswer
            question={question}
            value={answers[question.id]}
            onChange={value => setAnswer(question.id, value, question)}
            upload={interactive ? videoUpload : null}
            language={i18n.language}
            labelledBy={`q-label-${question.id}`}
          />
        ) : null}
        {question.type === QUESTION_TYPE.LOCATION ? (
          interactive ? (
            <LocationAnswer
              value={answers[question.id]}
              onChange={value => setAnswer(question.id, value, question)}
              language={i18n.language}
              labelledBy={`q-label-${question.id}`}
              required={question.required}
              invalid={
                checkedIds[question.id] &&
                !isQuestionAnswered(question, answers)
              }
            />
          ) : (
            <div className={styles.locationFields}>
              <Input
                readOnly
                placeholder={t("surveyManagement.widget.country_placeholder")}
              />
              <Input
                readOnly
                placeholder={t("surveyManagement.widget.city_placeholder")}
              />
            </div>
          )
        ) : null}
        {question.type === QUESTION_TYPE.OPEN_TEXT &&
        question.answerLength === OPEN_TEXT_LENGTH.SHORT ? (
          <Input
            value={interactive ? answers[question.id] || "" : undefined}
            readOnly={!interactive}
            maxLength={SHORT_TEXT_MAX_LENGTH}
            aria-required={question.required || undefined}
            aria-labelledby={`q-label-${question.id}`}
            onChange={e => setAnswer(question.id, e.target.value, question)}
            placeholder={t("surveyManagement.preview_text_placeholder")}
          />
        ) : null}
        {question.type === QUESTION_TYPE.OPEN_TEXT &&
        question.answerLength !== OPEN_TEXT_LENGTH.SHORT ? (
          <>
            <p className={styles.textHint}>
              {t("surveyManagement.widget.open_text_hint")}
            </p>
            {interactive ? (
              <>
                <TextArea
                  autoSize={{ minRows: 6, maxRows: 16 }}
                  value={answers[question.id] || ""}
                  maxLength={OPEN_TEXT_MAX_LENGTH}
                  aria-required={question.required || undefined}
                  aria-labelledby={`q-label-${question.id}`}
                  onChange={e =>
                    setAnswer(question.id, e.target.value, question)
                  }
                  placeholder={t("surveyManagement.preview_text_placeholder")}
                />
                <p className={styles.charCount} aria-live="polite">
                  {(answers[question.id] || "").length} / {OPEN_TEXT_MAX_LENGTH}
                </p>
              </>
            ) : (
              <textarea
                className={styles.previewTextarea}
                rows={3}
                readOnly
                placeholder={t("surveyManagement.preview_text_placeholder")}
              />
            )}
          </>
        ) : null}
      </div>
    );
  };

  const renderContactSection = (options = {}) => {
    const { steppedLayout = false } = options;
    if (!requestContactDetails) return null;
    const required = contactDetailsRequired === true;
    const isShaking = shakeTarget === "contact";
    return (
      <div
        className={[
          styles.contactSection,
          steppedLayout ? styles.contactSectionStep : "",
          isShaking ? styles.questionBlockError : ""
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <p className={styles.contactTitle} id="survey-contact-title">
          {t("surveyManagement.contact_section_title")}
          {required ? (
            <span className={styles.required} aria-hidden="true">
              *
            </span>
          ) : null}
        </p>
        {interactive ? (
          <div className={styles.contactGrid}>
            <Input
              value={contact.firstName || ""}
              onChange={e =>
                onContactFieldChange?.("firstName", e.target.value)
              }
              placeholder={t("surveyManagement.contact_first_name")}
              maxLength={120}
              aria-required={required || undefined}
              status={contactErrors.firstName ? "error" : undefined}
            />
            <Input
              value={contact.lastName || ""}
              onChange={e => onContactFieldChange?.("lastName", e.target.value)}
              placeholder={t("surveyManagement.contact_last_name")}
              maxLength={120}
              aria-required={required || undefined}
              status={contactErrors.lastName ? "error" : undefined}
            />
            <div className={styles.contactEmail}>
              <Input
                type="email"
                value={contact.email || ""}
                onChange={e => onContactFieldChange?.("email", e.target.value)}
                onBlur={onContactEmailBlur}
                placeholder={t("surveyManagement.contact_email")}
                maxLength={254}
                aria-required={required || undefined}
                status={contactErrors.email ? "error" : undefined}
              />
              {contactErrors.email === "invalid" ? (
                <p className={styles.fieldError} role="alert">
                  {t("surveyManagement.public.contact_invalid_email")}
                </p>
              ) : null}
            </div>
          </div>
        ) : (
          <div className={styles.contactGrid}>
            <div className={styles.previewContactField}>
              {t("surveyManagement.contact_first_name")}
            </div>
            <div className={styles.previewContactField}>
              {t("surveyManagement.contact_last_name")}
            </div>
            <div
              className={`${styles.previewContactField} ${styles.previewContactFieldFull}`}
            >
              {t("surveyManagement.contact_email")}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderBranding = () =>
    showBranding ? (
      <div className={styles.branding}>
        <a
          href="https://pivony.com"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.brandingLink}
        >
          <span>{t("surveyManagement.preview_powered_by")}</span>
          <img
            src={POWERED_BY_LOGO.light}
            alt="Pivony"
            className={styles.brandingLogo}
            draggable={false}
          />
        </a>
      </div>
    ) : null;

  const renderThankYou = () => {
    const messageText =
      (thankYouMessage || "").trim() ||
      t("surveyManagement.widget.thank_you_default");
    return (
      <div className={styles.thankYou}>
        <div className={styles.thankYouIcon} aria-hidden="true">
          ✓
        </div>
        <p className={styles.thankYouTitle}>
          {t("surveyManagement.public.thanks_title")}
        </p>
        <p className={styles.thankYouMessage}>{messageText}</p>
        {renderBranding()}
      </div>
    );
  };

  const showNext =
    stepIndex < totalSteps - 1 &&
    (browsable ||
      (interactive &&
        stepped &&
        currentQuestion &&
        !supportsAutoAdvance(currentQuestion.type)));
  const showBack = navigable && stepIndex > 0;
  const isLastStep = !stepped || stepIndex >= totalSteps - 1;
  const showSubmit = !stepped || isLastStep;
  const primaryLabel = submitting
    ? t("surveyManagement.widget.submitting")
    : isLastStep
    ? t("surveyManagement.preview_submit")
    : t("surveyManagement.widget.next");

  const renderStepContent = () => {
    if (onContactStep) {
      return (
        <div key={`step-contact-${stepAnimKey}`} className={styles.stepPanel}>
          {renderContactSection({ steppedLayout: true })}
        </div>
      );
    }
    if (currentQuestion) {
      return (
        <div key={`step-q-${stepAnimKey}`} className={styles.stepPanel}>
          {renderQuestion(currentQuestion, activeStep, { showIndex: true })}
        </div>
      );
    }
    return null;
  };

  const renderQuestionsBody = () => {
    if (stepped) {
      return (
        <div className={styles.questions}>
          {questionCount === 0 ? (
            <p className={styles.previewEmpty}>
              {t("surveyManagement.preview_empty")}
            </p>
          ) : (
            renderStepContent()
          )}
        </div>
      );
    }

    return (
      <>
        <div className={styles.questions}>
          {questionCount === 0 ? (
            <p className={styles.previewEmpty}>
              {t("surveyManagement.preview_empty")}
            </p>
          ) : (
            <>
              {visibleQuestions.map((q, idx) => renderQuestion(q, idx))}
              {hiddenCount > 0 ? (
                <p className={styles.previewMore}>
                  {t("surveyManagement.preview_more_questions", {
                    count: hiddenCount
                  })}
                </p>
              ) : null}
            </>
          )}
        </div>
        {renderContactSection()}
      </>
    );
  };

  return (
    <div
      ref={panelRef}
      tabIndex={interactive ? -1 : undefined}
      className={[
        styles.panel,
        embed ? styles.panelEmbed : "",
        compact ? styles.panelCompact : ""
      ]
        .filter(Boolean)
        .join(" ")}
      style={panelStyle}
    >
      <div
        className={[styles.header, !showHeaderBar ? styles.headerNoTitle : ""]
          .filter(Boolean)
          .join(" ")}
      >
        {showHeaderBar ? (
          <div className={styles.headerBrand}>
            {brandLogo ? (
              <img
                src={brandLogo}
                alt=""
                className={styles.headerLogo}
                draggable={false}
              />
            ) : null}
            {headerText ? (
              <span className={styles.headerTitle}>{headerText}</span>
            ) : null}
          </div>
        ) : null}
      </div>
      <div className={styles.body}>
        {submitted ? (
          renderThankYou()
        ) : (
          <>
            <h1 className={styles.title}>{title}</h1>
            {/* The description is the survey's introduction: shown once, on the first step. */}
            {description &&
            (!navigable || (activeStep ?? 0) === 0) ? (
              <p className={styles.desc}>{description}</p>
            ) : null}
            {deadlineNote && (!navigable || (activeStep ?? 0) === 0) ? (
              <p className={styles.deadline}>{deadlineNote}</p>
            ) : null}

            {submitError ? (
              <p className={styles.submitError} role="alert">
                {submitError}
              </p>
            ) : null}

            {interactive && gateSlot ? (
              <>
                <div className={styles.gate}>{gateSlot}</div>
                {renderBranding()}
              </>
            ) : (
              <>
                <SurveyProgressIndicator
                  total={totalSteps}
                  current={progressCurrent}
                  labelTotal={onContactStep ? totalSteps : questionCount}
                  labelCurrent={
                    onContactStep ? progressCurrent : (activeStep ?? 0) + 1
                  }
                  showProgressBar={stepped && totalSteps > 0}
                  isContactStep={!!onContactStep}
                />

                {renderQuestionsBody()}

                {interactive && showSubmit && consentSlot ? (
                  <div className={styles.consentRow}>{consentSlot}</div>
                ) : null}

                {interactive && showSubmit && captchaSlot ? (
                  <div className={styles.captchaRow}>{captchaSlot}</div>
                ) : null}

                <div className={styles.actionRow}>
                  {showBack ? (
                    <button
                      type="button"
                      className={styles.backBtn}
                      onClick={goBack}
                      disabled={submitting}
                    >
                      {t("surveyManagement.widget.back")}
                    </button>
                  ) : null}
                  {showNext ? (
                    <button
                      type="button"
                      className={styles.nextBtn}
                      onClick={handleNext}
                      disabled={submitting || checkingEmail}
                    >
                      {t("surveyManagement.widget.next")}
                    </button>
                  ) : null}
                  {showSubmit ? (
                    <button
                      type="button"
                      className={[
                        styles.submitBtn,
                        previewMode ? styles.submitBtnPreview : "",
                        showBack || showNext ? styles.submitBtnInline : ""
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      disabled={
                        interactive ? submitting || submitDisabled : true
                      }
                      onClick={interactive ? handlePrimarySubmit : undefined}
                      aria-busy={submitting || undefined}
                    >
                      {submitting ? (
                        <span className={styles.submitBtnInner}>
                          <span
                            className={styles.submitSpinner}
                            aria-hidden="true"
                          />
                          {primaryLabel}
                        </span>
                      ) : (
                        primaryLabel
                      )}
                    </button>
                  ) : null}
                </div>

                {!submitted ? renderBranding() : null}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SurveyWidgetPanel;
